/**
 * helpers.tsx — Utilidades presentacionales del CRM.
 *
 * Dos bloques:
 *  1) Funciones PURAS de estilo/icono (no dependen de idioma) → export directo.
 *  2) Hook useCrmFormatters(): formatos de fecha/hora atados al IDIOMA ACTIVO.
 *     El locale sale de i18n.language, NO de la región del usuario. Idioma y región
 *     son conceptos separados (ver i18n/config.ts): el idioma manda en fechas/números,
 *     la región manda en moneda.
 */

import React from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowUp, ArrowDown } from 'lucide-react'
import { TEMPERATURE_OPTIONS, ACTIVITY_TYPES } from './constants'
import type { Conversation } from './types'

// ────────────────────────────────────────────────
// 1) PUROS — sin idioma
// ────────────────────────────────────────────────

/** Clases Tailwind por estado del lead. Espejo de STATUS_OPTIONS.color. */
export const getStatusClasses = (status: string): string => {
  const classMap: Record<string, string> = {
    new: 'bg-yellow-500/20 text-yellow-400',
    contacted: 'bg-blue-500/20 text-blue-400',
    qualified: 'bg-green-500/20 text-green-400',
    negotiating: 'bg-orange-500/20 text-orange-400',
    closed_won: 'bg-emerald-500/20 text-emerald-400',
    closed_lost: 'bg-red-500/20 text-red-400'
  }
  return classMap[status] || 'bg-gray-500/20 text-gray-400'
}

/** Color sólido del borde superior de cada columna Kanban. */
export const getStatusAccent = (status: string): string => {
  const map: Record<string, string> = {
    new: 'bg-yellow-400',
    contacted: 'bg-blue-400',
    qualified: 'bg-green-400',
    negotiating: 'bg-orange-400',
    closed_won: 'bg-emerald-400',
    closed_lost: 'bg-red-400'
  }
  return map[status] || 'bg-gray-400'
}

export const getTemperatureIcon = (temp: string): string =>
  TEMPERATURE_OPTIONS.find(t => t.value === temp)?.icon || '🌡️'

export const getActivityIcon = (type: string): string =>
  ACTIVITY_TYPES.find(t => t.value === type)?.icon || '📌'

/** Color de la barra de score: verde ≥70, amarillo ≥40, rojo abajo. */
export const getScoreBarClass = (score: number): string =>
  score >= 70 ? 'bg-green-500' : score >= 40 ? 'bg-yellow-500' : 'bg-red-500'

interface SortIconProps { field: string; sortField: string; sortOrder: 'asc' | 'desc' }

/** Flecha de orden. Recibe el estado por props: los hijos no manejan el sort, solo lo pintan. */
export const SortIcon: React.FC<SortIconProps> = ({ field, sortField, sortOrder }) => {
  if (sortField !== field) return null
  return sortOrder === 'asc'
    ? <ArrowUp className="w-4 h-4 inline ml-1" />
    : <ArrowDown className="w-4 h-4 inline ml-1" />
}

// ────────────────────────────────────────────────
// 2) HOOK — formatos atados al idioma activo
// ────────────────────────────────────────────────

export const useCrmFormatters = () => {
  const { t, i18n } = useTranslation('crm')
  const locale = i18n.language?.startsWith('en') ? 'en-US' : 'es-MX'

  const formatDate = (dateString: string) => {
    if (!dateString) return t('noDate')
    return new Date(dateString).toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' })
  }

  const formatDateShort = (dateString: string) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString(locale, { month: 'short', day: 'numeric' })
  }

  const formatDateTime = (dateString: string) => {
    if (!dateString) return t('noDate')
    return new Date(dateString).toLocaleDateString(locale, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
  }

  const formatTime = (dateString: string) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
  }

  /** Preview del primer mensaje del cliente; si no hay, "Anónimo HH:MM". */
  const getConversationName = (conv: Conversation) => {
    const firstUserMsg = conv.messages?.find((m: any) => m.role === 'user')
    if (firstUserMsg?.content) {
      const preview = firstUserMsg.content.slice(0, 30)
      return preview.length < firstUserMsg.content.length ? preview + '...' : preview
    }
    return t('anonymous', { time: formatTime(conv.created_at) })
  }

  /** Etiqueta legible de una duración: "45 min" | "1 hora" | "1 h 30 min" | "2 horas". */
  const formatDuration = (minutes: number) => {
    if (minutes < 60) return t('agenda.minutes', { count: minutes })
    if (minutes === 90) return t('agenda.hourAndHalf')
    if (minutes % 60 === 0) return t('agenda.hours', { count: minutes / 60 })
    return `${Math.floor(minutes / 60)} h ${minutes % 60} min`
  }

  /** Hora de fin calculada — feedback inmediato al elegir duración (patrón Google Calendar). */
  const getEndTime = (startISO: string, durationMinutes: number) => {
    if (!startISO) return ''
    const end = new Date(new Date(startISO).getTime() + durationMinutes * 60000)
    return end.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
  }

  return { formatDate, formatDateShort, formatDateTime, formatTime, getConversationName, formatDuration, getEndTime, locale }
}
